import { datadogLogs } from '@datadog/browser-logs'
import { API_DISTRIBUTION_FACET } from 'utils/datadog'
import { RespondentEvent, RespondentStatus } from 'model/respondentStatus'
import { RESPONDENT_STATUS_API_PATH } from 'utils/constants'
import { getNowTimeString, getUtcOffset } from 'utils/datetime'
import getPersistedRespondentChoices from 'utils/getPersistedRespondentChoices'
import { legacyApiUrlEndpoint } from 'utils/helper'
import { QualityCheckResult } from './usePerformQualityCheck'
import { usePostCallbackWithRetry } from './usePostCallback'
import { useQuestionnaireParams } from './useQuestionnaireParams'

// Not all 400s mean "blocked" — only those where the backend explicitly
// rejects the status update (e.g. survey already completed or respondent
// in a terminal state). We check the message prefix to distinguish from
// other 400 errors (e.g. validation failures) which should still throw.
function isBlockedStatusError(err: unknown): boolean {
  const response = (err as any)?.response
  return (
    response?.status === 400 &&
    typeof response?.data?.message === 'string' &&
    response.data.message.startsWith('Blocked')
  )
}

const endpoint = `${legacyApiUrlEndpoint}${RESPONDENT_STATUS_API_PATH}`

const useSendRespondentStatus = () => {
  const mandatoryQueryParams = useQuestionnaireParams()

  const sendRespondentStatusInternal =
    usePostCallbackWithRetry<RespondentStatus>()

  const sendRespondentStatus: (
    respondentStatus: RespondentEvent,
    qualityCheckResult: QualityCheckResult
  ) => Promise<{ blocked: boolean }> = async (
    respondentStatus,
    qualityCheckResult
  ) => {
    if (!mandatoryQueryParams) {
      // NOTE: should never get here
      datadogLogs.logger.error(
        'Respondent status failed to be sent. Mandatory params missing. Ensure `sid`, `rid` and `ps` are specified.'
      )
      return { blocked: false }
    }

    datadogLogs.logger.info('Sending respondent status', {
      apiUsed: API_DISTRIBUTION_FACET.LEGACY
    })

    const payload: RespondentStatus = {
      respondentId: mandatoryQueryParams.respondentId,
      status: respondentStatus,
      surveyId: mandatoryQueryParams.surveyId,
      panelSupplierCode: mandatoryQueryParams.panelSupplier,
      isPreview: mandatoryQueryParams.preview,
      metaData: { qualityCheckResult },
      submissionDatetime: getNowTimeString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utcOffset: getUtcOffset()
    }

    const persistedRespondentChoices = getPersistedRespondentChoices(
      mandatoryQueryParams.respondentId,
      mandatoryQueryParams.surveyId
    )

    try {
      await sendRespondentStatusInternal(payload, endpoint)
      datadogLogs.logger.info(`Respondent status: ${respondentStatus}`, {
        responseChoices: persistedRespondentChoices
      })
      return { blocked: false }
    } catch (err) {
      const blocked = isBlockedStatusError(err)
      datadogLogs.logger[blocked ? 'warn' : 'error'](
        blocked
          ? 'Respondent status returned 400 (terminal status)'
          : 'Network Failure: Respondent status failed to be sent',
        {
          responseChoices: persistedRespondentChoices,
          err,
          payload,
          payloadSize: JSON.stringify(payload).length
        }
      )
      if (!blocked) throw err
      return { blocked: true }
    }
  }

  return sendRespondentStatus
}

export default useSendRespondentStatus
